import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, ArrowDownRight, ArrowLeftRight, ArrowUpRight, Boxes,
  PackageCheck, Plus, Receipt,
} from "lucide-react";

import PageGate from "../../../components/PageGate";
import AccessGate from "../../../components/AccessGate";
import { PERMS } from "../../../utils/perms";
import { describeApiError } from "../../../lib/apiError";
import { useAuth } from "../../../context/AuthContexts";
import StoreShell from "../components/StoreShell";
import MovementModal from "../components/MovementModal";
import StockActionModal, { type StockAction } from "../components/StockActionModal";
import { fmtDate, fmtQty, movementColor, movementLabel, recipient } from "../utils/stores";
import { useStoreGetDashboardQuery, useStoreGetStoresQuery } from "../api/storesApi";

const naira = (n: number) =>
  `₦${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

/**
 * One KPI.
 *
 * `attention` is what the amber card in the design is for: exactly one number
 * on this page is usually the one to act on, and colouring the others too
 * would make none of them stand out.
 */
function Kpi({
  label, value, hint, attention, to,
}: {
  label: string;
  value: string;
  hint?: React.ReactNode;
  attention?: boolean;
  to?: string;
}) {
  const body = (
    <div
      className={`rounded-xl border p-4 h-full transition-colors ${
        attention ? "bg-amber-50 border-amber-300" : "bg-white hover:border-gray-300"
      }`}
    >
      <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${attention ? "text-amber-700" : "text-gray-900"}`}>
        {value}
      </div>
      {hint && <div className="text-xs text-gray-400 mt-0.5">{hint}</div>}
    </div>
  );

  return to ? <Link to={to} className="block h-full">{body}</Link> : body;
}

/**
 * Seven days of sales, drawn inline.
 *
 * An SVG polyline rather than a chart library: one series, seven points, no
 * axes worth the name. Pulling in a charting dependency for this would cost
 * more to load than the page it sits on.
 */
function SalesTrend({ series }: { series: any[] }) {
  const points = useMemo(() => {
    if (!series.length) return "";
    const max = Math.max(...series.map((d) => Number(d.amount) || 0), 1);
    const w = 100;
    const h = 40;
    return series
      .map((d, i) => {
        const x = series.length === 1 ? w / 2 : (i / (series.length - 1)) * w;
        const y = h - ((Number(d.amount) || 0) / max) * (h - 4) - 2;
        return `${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(" ");
  }, [series]);

  const total = series.reduce((n, d) => n + (Number(d.amount) || 0), 0);

  return (
    <div className="bg-white border rounded-xl p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-bold text-gray-800">Sales, last 7 days</h2>
          <p className="text-xs text-gray-400 mt-0.5">{naira(total)} in total</p>
        </div>
      </div>

      {total > 0 ? (
        <>
          <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-28 mt-4">
            <polyline
              points={points}
              fill="none"
              stroke="#4f46e5"
              strokeWidth="1.2"
              vectorEffect="non-scaling-stroke"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          </svg>
          <div className="flex justify-between mt-1">
            {series.map((d) => (
              <div key={d.date} className="text-[10px] text-gray-400 text-center flex-1">
                {d.label}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="h-28 flex items-center justify-center text-xs text-gray-400">
          No sales recorded in the last seven days.
        </div>
      )}
    </div>
  );
}

function StoreHomeInner() {
  const { user } = useAuth();
  const { data: stores = [] } = useStoreGetStoresQuery();
  const [storeId, setStoreId] = useState("");
  const { data, isLoading, error } = useStoreGetDashboardQuery(
    storeId ? { store_id: storeId } : undefined,
  );

  const [receiving, setReceiving] = useState(false);
  const [action, setAction] = useState<StockAction | null>(null);

  const salesToday = Number(data?.sales_today?.amount ?? 0);
  const salesYesterday = Number(data?.sales_yesterday?.amount ?? 0);
  const delta = salesToday - salesYesterday;

  const firstName = String((user as any)?.name ?? "").split(" ")[0] || "there";

  return (
    <StoreShell
      title={`${greeting()}, ${firstName}`}
      subtitle="What the stores hold today, what is running out, and what is out with staff."
      actions={
        stores.length > 1 ? (
          <select
            value={storeId}
            onChange={(e) => setStoreId(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-gray-100"
          >
            <option value="">All stores</option>
            {stores.map((s: any) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        ) : null
      }
    >
      {error && (
        <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm font-medium text-red-700">
          {describeApiError(error, "Could not load the store dashboard.")}
        </div>
      )}

      {isLoading && (
        <div className="py-14 text-center text-sm text-gray-400 animate-pulse">Loading…</div>
      )}

      {data && (
        <>
          {/* KPI band */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi
              label="Sales today"
              value={naira(salesToday)}
              hint={
                salesYesterday === 0 && salesToday === 0 ? (
                  "Nothing sold yet today"
                ) : (
                  <span className={`inline-flex items-center gap-1 ${delta >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                    {delta >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                    {naira(Math.abs(delta))} vs yesterday
                  </span>
                )
              }
              to="/stores/sales"
            />
            <Kpi
              label="Stock value"
              value={naira(data.stock?.value ?? 0)}
              hint={`${fmtQty(data.stock?.units ?? 0)} units on the shelves`}
              to="/stores/inventory"
            />
            <Kpi
              label="Low-stock items"
              value={String(data.low_stock_count ?? 0)}
              hint="below reorder level"
              attention={(data.low_stock_count ?? 0) > 0}
              to="/stores/items"
            />
            <Kpi
              label="Out with staff"
              value={fmtQty(data.with_staff?.units ?? 0)}
              hint={
                data.returns_pending > 0
                  ? `${data.returns_pending} return${data.returns_pending === 1 ? "" : "s"} waiting`
                  : `${data.with_staff?.holders ?? 0} holding stock`
              }
              to="/stores/staff"
            />
          </div>

          {/* Trend + quick actions */}
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <SalesTrend series={data.series ?? []} />
            </div>

            <AccessGate
              perm={PERMS.store.movement}
              fallback={
                <div className="bg-white border rounded-xl p-5 text-xs text-gray-400">
                  You can read the register but not record movements.
                </div>
              }
            >
              <div className="bg-white border rounded-xl p-5">
                <h2 className="font-bold text-gray-800 mb-3">Quick actions</h2>
                <div className="space-y-2">
                  <button
                    onClick={() => setAction("sale")}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Receipt className="w-4 h-4 text-emerald-600" /> New sale
                  </button>
                  <Link
                    to="/stores/items"
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Plus className="w-4 h-4 text-gray-500" /> Add product
                  </Link>
                  <button
                    onClick={() => setReceiving(true)}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Boxes className="w-4 h-4 text-blue-600" /> Receive stock
                  </button>
                  <button
                    onClick={() => setAction("transfer")}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <ArrowLeftRight className="w-4 h-4 text-indigo-600" /> Move stock
                  </button>
                  <button
                    onClick={() => setAction("issue")}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <PackageCheck className="w-4 h-4 text-gray-500" /> Issue to staff
                  </button>
                </div>
              </div>
            </AccessGate>
          </div>

          {/* The two lists a manager clears */}
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-white border rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-gray-800">Low stock</h2>
                <Link to="/stores/items" className="text-xs font-medium text-indigo-600">View all</Link>
              </div>

              {(data.low_stock_items ?? []).length === 0 ? (
                <p className="text-xs text-gray-400 py-6 text-center">
                  Nothing is below its reorder level.
                </p>
              ) : (
                <div className="divide-y divide-gray-50">
                  {(data.low_stock_items ?? []).slice(0, 6).map((i: any) => (
                    <div key={i.id} className="flex items-center justify-between gap-3 py-2.5">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{i.name}</div>
                        <div className="text-xs text-gray-400">
                          {fmtQty(i.quantity)} {i.unit} left · reorder at {fmtQty(i.reorder_level)}
                        </div>
                      </div>
                      <AccessGate perm={PERMS.store.movement}>
                        <button
                          onClick={() => setReceiving(true)}
                          className="text-xs font-medium text-amber-700 shrink-0 inline-flex items-center gap-1"
                        >
                          <AlertTriangle className="w-3 h-3" /> Restock
                        </button>
                      </AccessGate>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white border rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-gray-800">Recent activity</h2>
                <Link to="/stores/movements" className="text-xs font-medium text-indigo-600">View all</Link>
              </div>

              {(data.recent_movements ?? []).length === 0 ? (
                <p className="text-xs text-gray-400 py-6 text-center">Nothing has moved yet.</p>
              ) : (
                <div className="divide-y divide-gray-50">
                  {(data.recent_movements ?? []).map((m: any) => (
                    <div key={m.id} className="flex items-center justify-between gap-3 py-2.5">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {m.item?.name ?? "—"}
                        </div>
                        <div className="text-xs text-gray-400 truncate">
                          {fmtDate(m.movement_date)}
                          {m.store?.name ? ` · ${m.store.name}` : ""}
                          {m.holder_type === "admin" ? ` · ${recipient(m)}` : ""}
                        </div>
                      </div>
                      <span
                        className={`shrink-0 inline-flex items-center px-2 py-0.5 text-[11px] border rounded-full ${movementColor(m.type)}`}
                      >
                        {movementLabel(m.type)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {receiving && <MovementModal type="in" onClose={() => setReceiving(false)} />}
      {action && <StockActionModal action={action} onClose={() => setAction(null)} />}
    </StoreShell>
  );
}

export default function StoreHomePage() {
  return (
    <PageGate perm={PERMS.store.access}>
      <StoreHomeInner />
    </PageGate>
  );
}
